import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, tap } from 'rxjs';
import { Injectable } from '@angular/core';

import {
  DprConsumable,
  DprDetails,
  DprHse,
  DprPersonnel,
  DprProgress,
  DprProgressWithoutTarget,
  DprStatusTracker,
  DprTableOfColumns,
  DprWeather,
} from '@shared/models/dpr';
import { AppFacade } from '@shared/state/facades';
import { DprDetailsBasePersistenceService } from './persistance/dpr-details-base-persistence.service';
import { DprDetailsRemotePersistenceService } from './persistance/dpr-details-remote-persistence.service';
import { DprDetailsLocalPersistenceService } from './persistance/dpr-details-local-persistence.service';
import { CopyPreviousDprFilters } from '@dpr-details/models/copy-previous-dpr-filters.model';
import {
  DprActivitiesGridSync,
  DprActivitiesGridSyncResult,
  DprAppendixGridSync,
  DprAppendixGridSyncResult,
  DprConsumableGridSync,
  DprHiddenGridSync,
  DprForecastActivitiesGridSync,
  DprForecastActivitiesGridSyncResult,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprProgressGridSync,
  DprSummaryGridSyncResult,
  DprWeatherGridSync,
  DprStatusTrackerGridSync,
  DprProgressWithoutTargetGridSync,
  DprFreeTextFieldSyncResult,
  DprFreeTextFieldSync,
  DprHiddenGridSyncResult,
  DprSummaryGridSync,
} from '@dpr-details/models/sync';
import {
  CopyPreviousDprForecastActivitiesOptions,
  CopyPreviousDprForecastActivitiesResult,
  DprActivityCopyOption,
} from '@dpr-details/models';
import {
  HttpDprDetailsContext,
  UtcDateTimeOptions,
  dynamicTextPlaceholder,
} from '@shared/enums';
import { DprTitlePipe } from '@shared/pipes';
import { convertDateToString, toUTCDate } from '@shared/utils';
import { GenericHttpClientService } from '@shared/services';

@UntilDestroy()
@Injectable()
export class DprDetailsService {
  action: DprDetailsBasePersistenceService;

  constructor(
    private appFacade: AppFacade,
    private dprDetailsRemotePersistenceService: DprDetailsRemotePersistenceService,
    private dprDetailsLocalPersistenceService: DprDetailsLocalPersistenceService,
    private genericHttpClientService: GenericHttpClientService,
    private dprTitlePipe: DprTitlePipe
  ) {
    this.init();
  }

  //#region Persistance

  getDprDetails(dprDetailId: number | string): Observable<DprDetails> {
    return this.action.getDprDetails(dprDetailId);
  }

  syncDprActivities(
    data: DprActivitiesGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprActivitiesGridSyncResult> {
    return this.action.syncDprActivities(data, isAutoSyncFromAdminChange);
  }

  syncDprStatusTracker(
    data: DprStatusTrackerGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprStatusTracker> {
    return this.action.syncDprStatusTracker(data, isAutoSyncFromAdminChange);
  }

  syncDprWeather(
    data: DprWeatherGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprWeather> {
    return this.action.syncDprWeather(data, isAutoSyncFromAdminChange);
  }

  syncDprHseRegistrations(
    data: DprHseGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprHse> {
    return this.action.syncDprHse(data, isAutoSyncFromAdminChange);
  }

  syncDprPersonnel(
    data: DprPersonnelGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprPersonnel> {
    return this.action.syncDprPersonnel(data, isAutoSyncFromAdminChange);
  }

  syncDprConsumable(
    data: DprConsumableGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprConsumable> {
    return this.action.syncDprConsumable(data, isAutoSyncFromAdminChange);
  }

  syncDprSummary(
    data: DprSummaryGridSync,
    isExternalCommentSync: boolean,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprSummaryGridSyncResult> {
    return isExternalCommentSync
      ? this.action.syncDprSummaryExternalComment(
          data,
          isAutoSyncFromAdminChange
        )
      : this.action.syncDprSummary(data, isAutoSyncFromAdminChange);
  }

  syncDprForecastActivities(
    data: DprForecastActivitiesGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprForecastActivitiesGridSyncResult> {
    return this.action.syncDprForecastActivities(
      data,
      isAutoSyncFromAdminChange
    );
  }

  copyDprForecastActivitiesFromPreviousDpr(
    options: CopyPreviousDprForecastActivitiesOptions
  ): Observable<CopyPreviousDprForecastActivitiesResult> {
    return this.action.copyDprForecastActivitiesFromPreviousDpr(options);
  }

  syncDprFreeTextField(
    data: DprFreeTextFieldSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprFreeTextFieldSyncResult> {
    return this.action.syncDprFreeTextField(data, isAutoSyncFromAdminChange);
  }

  syncDprProgress(
    data: DprProgressGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprProgress> {
    return this.action.syncDprProgress(data, isAutoSyncFromAdminChange);
  }

  syncDprProgressWithoutTarget(
    data: DprProgressWithoutTargetGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprProgressWithoutTarget> {
    return this.action.syncDprProgressWithoutTarget(
      data,
      isAutoSyncFromAdminChange
    );
  }

  syncDprTableOfColumn(
    data: any,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprTableOfColumns> {
    return this.action.syncDprTableOfColumns(data, isAutoSyncFromAdminChange);
  }

  syncDprAppendix(
    data: DprAppendixGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprAppendixGridSyncResult> {
    return this.action.syncDprAppendix(data, isAutoSyncFromAdminChange);
  }

  syncDprHidden(
    data: DprHiddenGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprHiddenGridSyncResult> {
    return this.action.syncDprHidden(data, isAutoSyncFromAdminChange);
  }

  getPreviousDprDetailsId(dprDate: Date): Observable<number | string> {
    return this.action.getPreviousDprDetailsId(dprDate);
  }

  getNextDprDetailsId(dprDate: Date): Observable<number | string> {
    return this.action.getNextDprDetailsId(dprDate);
  }

  //#endregion

  copyDprFromPrevious(
    dprDetailsSectionsFilters: CopyPreviousDprFilters
  ): Observable<DprDetails> {
    return this.genericHttpClientService.post<
      DprDetails,
      CopyPreviousDprFilters
    >({
      context: HttpDprDetailsContext.CopyDprFromPrevious,
      body: dprDetailsSectionsFilters,
    });
  }

  copySelectedActivities(
    options: DprActivityCopyOption
  ): Observable<DprDetails> {
    return this.genericHttpClientService.post<
      DprDetails,
      DprActivityCopyOption
    >({
      context: HttpDprDetailsContext.CopySelectedActivities,
      body: options,
    });
  }

  isFirstDpr(dprDate: Date): Observable<boolean> {
    return this.action.isFirstDpr(dprDate);
  }

  getDprDetailsId(dprId: number | string, date: Date): Observable<number> {
    const utcDate = convertDateToString(
      toUTCDate(new Date(date), UtcDateTimeOptions.UtcDateWithoutTime)
    );
    return this.genericHttpClientService.get<number>({
      context: HttpDprDetailsContext.GetDprDetailsId,
      args: {
        utcDate,
        dprId,
      },
    });
  }

  getModalTitle(
    displayId: number,
    dprDate: string,
    dprDetailRevision: number
  ): string {
    const title = $localize`:@@shared.dprReportService.titleReportHistoryModal:DPR #(---), (---), Revision (---)`;
    return title
      .replace(dynamicTextPlaceholder, this.dprTitlePipe.transform(displayId))
      .replace(dynamicTextPlaceholder, dprDate)
      .replace(dynamicTextPlaceholder, dprDetailRevision.toString());
  }

  //#region  Private methods

  private init(): void {
    this.appFacade.query.isAppOnline$
      .pipe(
        untilDestroyed(this),
        tap((isAppOnline) => {
          if (!this.action) {
            this.setAction(isAppOnline);
          } else {
            this.setAction(isAppOnline);
          }
        })
      )
      .subscribe();
  }

  private setAction(isAppOnline: boolean): void {
    this.action = isAppOnline
      ? this.dprDetailsRemotePersistenceService
      : this.dprDetailsLocalPersistenceService;
  }

  //#endregion
}
